import type { Comment as CommentType } from '@/@types/comment'
import { ContentType } from '@/@types/common'
import { useSelector } from '@tanstack/react-store'
import commentOrderStore from '@/utils/stores/commentOrder'
import { type GetCommentParams, getNonPrimaryCommentsQueryOptions } from '@/utils/fetchers/comment'
import { useInfiniteQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { flatMap, map, pick, uniqBy } from 'lodash-es'
import { AnimatePresence, motion } from 'motion/react'
import type { HTMLAttributes, MouseEvent, ReactNode } from 'react'
import { Button } from '@mantine/core'
import styles from './CommentReplies.module.css'

export interface CommentRepliesProps extends HTMLAttributes<HTMLDivElement> {
	data: CommentType
	renderComment: (data: GetCommentParams) => ReactNode
}

export default function CommentReplies({
	data,
	renderComment,
	className,
	...props
}: CommentRepliesProps) {
	const order = useSelector(commentOrderStore, (selectedOrder) =>
		data.targetType === ContentType.STORY ? undefined : selectedOrder
	)
	const { hotReplies = [], replyCount } = data
	const primaryCommentId = data.level <= 1 ? data.id : data.threadId

	const {
		data: replyPages,
		fetchNextPage,
		hasNextPage,
		isFetching,
		refetch,
	} = useInfiniteQuery({
		...getNonPrimaryCommentsQueryOptions({
			targetType: data.targetType,
			primaryCommentId,
			level: data.level,
			limit: 5,
			targetId: data.targetId,
			order,
		}),
		enabled: order === 'TIME' && hotReplies.length > 0,
	})
	const commentReplies = map(
		uniqBy(
			[...(order !== 'TIME' ? hotReplies : []), ...flatMap(replyPages?.pages, (item) => item.data)],
			'id'
		),
		(item) => pick(item, ['id', 'targetType']) as GetCommentParams
	)
	const hasLoadedReplyPage = Boolean(replyPages?.pages.length)

	const handleLoadMore = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation()
		void (hasLoadedReplyPage ? fetchNextPage() : refetch())
	}

	if (commentReplies.length === 0 && replyCount === 0) {
		return null
	}

	return (
		<div className={clsx(styles.root, className)} {...props}>
			<AnimatePresence mode="popLayout">
				{/* comment replies */}
				{map(commentReplies, (item) => (
					<motion.div
						key={item.id}
						initial={{
							opacity: 0,
						}}
						animate={{
							opacity: 1,
						}}
						exit={{
							opacity: 0,
						}}
					>
						{renderComment(item)}
					</motion.div>
				))}

				{(hasNextPage || replyCount > commentReplies.length) && (
					<motion.div
						initial={{
							opacity: 0,
						}}
						animate={{
							opacity: 1,
						}}
						exit={{
							opacity: 0,
						}}
						className={styles.loadMoreButton}
					>
						<Button
							onClick={handleLoadMore}
							variant="outline"
							size="sm"
							loading={isFetching}
							disabled={isFetching}
						>
							加载更多
						</Button>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	)
}
